import React, { useState, useRef } from "react";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import AdSense from "../components/AdSense";
import AboutSection from "../components/Home/AboutSection";
import TestimonialsSection from "../components/Home/TestimonialsSection";
import FAQSection from "../components/Home/ FAQSection";
import IDMergeSection from "../components/Home/IDMergeSection";
import Seo from "../seo/seo";
import logo from "../assets/merge-image.webp";
import { TemplateSelector } from "../components/Collage";
import { CollageTemplateType } from "../utils/constants";
import { useNavigate } from "react-router-dom";

const A4_WIDTH_PX = 794;
const A4_HEIGHT_PX = 1123;
const MARGIN_PX = 10;
const MAX_FILE_SIZE = 10 * 1024 * 1024;

interface SectionData {
  id: number;
  image?: string;
  error?: string;
}

const Spinner: React.FC = () => (
  <div className="fixed inset-0 flex items-center justify-center bg-gray-500 bg-opacity-50 z-50">
    <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
  </div>
);

const MainPage: React.FC = () => {
  const [sections, setSections] = useState<SectionData[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const printableRef = useRef<HTMLDivElement | null>(null);
 const [selectedTemplate, setSelectedTemplate] = useState<CollageTemplateType>("grid2x2");
  const navigate = useNavigate();

  const handleStart = () => {
    navigate("/collages", { state: { template: selectedTemplate } });
  };

  const optimizeImage = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      if (file.size > MAX_FILE_SIZE) {
        reject(new Error("File size exceeds 10MB limit"));
        return;
      }

      const img = new Image();
      const reader = new FileReader();

      reader.onload = (e) => {
        img.src = e.target?.result as string;
        img.onload = () => {
          const canvas = document.createElement("canvas");
          const ctx = canvas.getContext("2d");
          const maxDimension = Math.min(
            A4_WIDTH_PX - 2 * MARGIN_PX,
            A4_HEIGHT_PX - 2 * MARGIN_PX
          );

          // Maintain aspect ratio
          let width = img.width;
          let height = img.height;
          if (width > height) {
            if (width > maxDimension) {
              height = (height * maxDimension) / width;
              width = maxDimension;
            }
          } else {
            if (height > maxDimension) {
              width = (width * maxDimension) / height;
              height = maxDimension;
            }
          }

          canvas.width = width;
          canvas.height = height;
          ctx?.drawImage(img, 0, 0, width, height);
          resolve(canvas.toDataURL("image/jpeg", 0.9));
        };
      };
      reader.onerror = () => reject(new Error("Failed to read file"));
      reader.readAsDataURL(file);
    });
  };

  const handleImageUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = event.target.files;
    if (!files) return;

    setIsLoading(true);
    const newSectionsPromises = Array.from(files).map(async (file, index) => {
      try {
        const imageUrl = await optimizeImage(file);
        return {
          id: sections.length + index + 1,
          image: imageUrl,
        };
      } catch (error) {
        return {
          id: sections.length + index + 1,
          error: error instanceof Error ? error.message : "Processing failed",
        };
      }
    });

    const newSections = await Promise.all(newSectionsPromises);
    setSections((prevSections) => [...prevSections, ...newSections]);
    setIsLoading(false);
  };

  const handleExport = async (type: "pdf" | "image") => {
    if (!printableRef.current) return;

    setIsLoading(true);
    try {
      const canvas = await html2canvas(printableRef.current, {
        scale: 2,
        useCORS: true,
        logging: false,
        width: A4_WIDTH_PX,
        height: A4_HEIGHT_PX,
        windowWidth: A4_WIDTH_PX,
        windowHeight: A4_HEIGHT_PX,
      });

      const imgData = canvas.toDataURL("image/png");

      if (type === "pdf") {
        const pdf = new jsPDF("p", "mm", "a4");
        const pdfWidth = 210;
        const pdfHeight = 297;
        const marginMM = MARGIN_PX * 0.264583;

        const canvasAspect = canvas.width / canvas.height;
        const pdfAspect =
          (pdfWidth - 2 * marginMM) / (pdfHeight - 2 * marginMM);

        let imgWidth, imgHeight;
        if (canvasAspect > pdfAspect) {
          imgWidth = pdfWidth - 2 * marginMM;
          imgHeight = imgWidth / canvasAspect;
        } else {
          imgHeight = pdfHeight - 2 * marginMM;
          imgWidth = imgHeight * canvasAspect;
        }

        const xOffset = (pdfWidth - imgWidth) / 2;
        const yOffset = (pdfHeight - imgHeight) / 2;

        pdf.addImage(imgData, "PNG", xOffset, yOffset, imgWidth, imgHeight);
        pdf.save("layout.pdf");
      } else {
        const link = document.createElement("a");
        link.href = imgData;
        link.download = "layout.png";
        link.click();
      }
    } catch (error) {
      console.error("Export failed:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const renderImages = () => {
    const count = sections.length;

    if (count === 0) {
      return <div className="">No images uploaded yet</div>;
    }

    if (count === 1) {
      return (
        <div
          style={{
            height: "100%",
            padding: `${MARGIN_PX}px`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <img
            src={sections[0].image}
            alt="Content 1"
            style={{
              maxWidth: "100%",
              maxHeight: "100%",
              objectFit: "contain",
            }}
          />
        </div>
      );
    }

    if (count === 2) {
      return (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            height: "100%",
            gap: "2px",
            padding: `${MARGIN_PX}px`,
          }}
        >
          {sections.map((section, index) => (
            <div
              key={index}
              style={{
                flex: "1 1 50%",
                overflow: "hidden",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <img
                src={section.image}
                alt={`Content ${index + 1}`}
                style={{
                  maxWidth: "100%",
                  maxHeight: "100%",
                  objectFit: "contain",
                }}
              />
            </div>
          ))}
        </div>
      );
    }

    if (count === 3) {
      return (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            height: "100%",
            gap: "2px",
            padding: `${MARGIN_PX}px`,
          }}
        >
          <div
            style={{
              height: "50%",
              overflow: "hidden",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <img
              src={sections[0].image}
              alt="Content 1"
              style={{
                maxWidth: "100%",
                maxHeight: "100%",
                objectFit: "contain",
              }}
            />
          </div>
          <div
            style={{
              display: "flex",
              height: "50%",
              gap: "2px",
            }}
          >
            {sections.slice(1, 3).map((section, index) => (
              <div
                key={index}
                style={{
                  width: "50%",
                  height: "100%",
                  overflow: "hidden",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <img
                  src={section.image}
                  alt={`Content ${index + 2}`}
                  style={{
                    maxWidth: "100%",
                    maxHeight: "100%",
                    objectFit: "contain",
                  }}
                />
              </div>
            ))}
          </div>
        </div>
      );
    }

    if (count > 3) {
      const sqrt = Math.sqrt(count);
      const cols = Math.ceil(sqrt);
      const rows = Math.ceil(count / cols);

      return (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: `repeat(${cols}, 1fr)`,
            gridTemplateRows: `repeat(${rows}, 1fr)`,
            height: "100%",
            width: "100%",
            gap: "2px",
            padding: `${MARGIN_PX}px`,
          }}
        >
          {sections.map((section, index) => (
            <div
              key={index}
              style={{
                overflow: "hidden",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <img
                src={section.image}
                alt={`Content ${index + 1}`}
                style={{
                  maxWidth: "100%",
                  maxHeight: "100%",
                  objectFit: "contain",
                }}
              />
            </div>
          ))}
        </div>
      );
    }
  };

  return (
    <div className="flex flex-col items-center min-h-screen bg-gradient-to-br from-gray-50 via-white to-blue-50 relative px-4">
      {isLoading && <Spinner />}

      <div className="w-full max-w-7xl backdrop-blur-lg border border-white/30 rounded-3xl py-20 sm:py-24 lg:py-32 transition-all duration-300">
        <Seo
          title="Combine & Print Multiple Images on One Page | A4, Letter & Legal Layout Tool"
          description="Combine multiple images into a single printable page using Stackasian's free online layout tool. Create high-quality designs for A4, Letter, or Legal sizes — perfect for photo collages, ID sheets, and project documents."
          keywords="combine images on one page, merge images for print, A4 photo layout, printable image sheet, letter size photo collage, legal size layout, image merge tool, online photo arranger"
          canonical="https://merge-images.stackasian.com/"
          ogTitle="Combine & Print Multiple Images | Stackasian A4 Layout Tool"
          ogDescription="Combine and export multiple images into a clean, printable layout with Stackasian. Supports A4, Letter, and Legal paper sizes — perfect for collages and presentations."
          ogUrl="https://merge-images.stackasian.com/"
          ogImage={logo}
          twitterTitle="Print Multiple Images on One Page | Free A4 Layout Tool"
          twitterDescription="Combine and print images in A4, Letter, or Legal formats with Stackasian's easy-to-use layout creator. Great for collages, ID sheets, and more."
          twitterImage={logo}
        />

        <h1 className="text-center text-transparent bg-clip-text bg-black font-bold leading-tight mb-6 text-3xl sm:text-4xl md:text-5xl lg:text-6xl px-4 sm:px-10 md:px-20 lg:px-40">
          Combine & Print Multiple Images on One Page – A4, Letter & Legal Sizes
        </h1>

        <p className="text-gray-700 text-center text-sm sm:text-base leading-relaxed px-4 sm:px-12 md:px-24 lg:px-72 mb-10 sm:mb-16">
          Upload and arrange your images effortlessly with our online photo
          layout tool. Export high-quality, printable designs in A4, Letter, or
          Legal formats. Ideal for creating collages, project documents, photo
          sheets, and professional portfolios – all directly in your browser.
        </p>

        {/* Upload Button */}
        <div className="flex flex-col items-center justify-center mb-8">
          <label
            htmlFor="fileUpload"
            className="inline-flex items-center space-x-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white font-medium py-3 px-6 rounded-full shadow-lg hover:from-blue-600 hover:to-purple-700 transition-transform transform hover:scale-105 cursor-pointer"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2}
              stroke="currentColor"
              className="w-6 h-6"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 16v-8m-4 4h8M3 16.5V19a2 2 0 002 2h14a2 2 0 002-2v-2.5m-1.5-4.5L12 4m0 0L4.5 12"
              />
            </svg>
            <span>Upload Images (Max 10MB)</span>
          </label>
          <input
            id="fileUpload"
            type="file"
            accept="image/*"
            multiple
            onChange={handleImageUpload}
            className="hidden"
            disabled={isLoading}
          />
        </div>

        {/* Rendered Layout Preview */}
        {sections.length > 0 && (
          <div
            ref={printableRef}
            className="relative bg-white rounded-xl border shadow-inner mx-auto overflow-hidden"
            style={{
              width: `${A4_WIDTH_PX}px`,
              height: `${A4_HEIGHT_PX}px`,
            }}
          >
            {renderImages()}
          </div>
        )}

        {/* Export Buttons */}
        <div className="mt-8 text-center flex flex-wrap justify-center gap-4">
          <button
            onClick={() => handleExport("pdf")}
            className="bg-[#3b82f6] text-white px-6 py-3 rounded-lg shadow-md hover:bg-blue-700 transition disabled:opacity-50"
            disabled={isLoading}
          >
            Download as PDF
          </button>
          <button
            onClick={() => handleExport("image")}
            className="bg-[#22c55e] text-white px-6 py-3 rounded-lg shadow-md hover:bg-emerald-700 transition disabled:opacity-50"
            disabled={isLoading}
          >
            Download as Image
          </button>
        </div>
      </div>

      {/* Ad & Additional Sections */}
      <div className="w-full max-w-7xl mt-10 px-4">
        <AdSense
          client="ca-pub-7582359274073375"
          slot="7347822224"
          style={{ display: "block", textAlign: "center" }}
          responsive={true}
        />
         <section className="bg-blue-50 py-16 text-center">
      <h2 className="text-3xl font-bold mb-4">Make Your Own Collage</h2>
      <p className="text-lg text-gray-700 mb-6">
        Choose a template and upload your images to create a beautiful collage.
      </p>

      {/* Grid Selector */}
      <div className="max-w-4xl mx-auto mb-6">
        <TemplateSelector selectedTemplate={selectedTemplate} onSelect={setSelectedTemplate} />
      </div>

      <button
        onClick={handleStart}
        className="inline-block px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition"
      >
        Start Creating
      </button>
    </section>
        <IDMergeSection />
        <AboutSection />
        <TestimonialsSection />
        <FAQSection />
      </div>
    </div>
  );
};

export default MainPage;
